import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MODEL_PROVIDERS,
  PROVIDER_LABEL,
  providerReady,
  type AssistantProvider,
  type AssistantSettings,
  type ModelProvider,
} from '@domain/entities/AssistantSettings';
import { getServices } from '@infrastructure/services';
import { Banner, Button, Chip, Spinner, cx, inputClass } from '../../design-system';
import { reasonFor } from '../../format';
import { useToast } from '../../providers/ToastProvider';
import styles from './AssistantSettingsTab.module.css';

/**
 * Configuración global › Asistente: con qué entiende el asistente de WhatsApp lo
 * que le escriben, y las llaves de quien lo entiende.
 *
 * Dos decisiones que son del superusuario y de nadie más. La primera, **a qué
 * empresa se le manda lo que la gente escribe**: a ninguna —palabras clave—, a
 * OpenAI o a Anthropic. La segunda, las llaves con que se paga.
 *
 * Una llave se pega aquí y no se vuelve a ver: el servidor la guarda cifrada y de
 * vuelta sólo dice en qué termina. Por eso el campo siempre está vacío, y por eso
 * hay un «Probar»: es la única forma de saber si la que está guardada sirve.
 */

const QUERY_KEY = ['assistant-settings'] as const;
const DAY = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

interface ProviderOption {
  readonly id: AssistantProvider;
  readonly title: string;
  readonly note: string;
}

const optionsFor = (settings: AssistantSettings): readonly ProviderOption[] => [
  {
    id: 'none',
    title: 'Palabras clave',
    note: 'Sin modelo de lenguaje. Entiende frases directas —«mi saldo», «acepta las de Gómez»— y lo que se escribe no se le manda a nadie más.',
  },
  ...MODEL_PROVIDERS.map(
    (provider): ProviderOption => ({
      id: provider,
      title: `${PROVIDER_LABEL[provider]} · ${settings.models[provider]}`,
      note: `Lenguaje natural. Lo que la gente le escriba al asistente se envía a ${PROVIDER_LABEL[provider]} para saber qué pidió; las cifras las pone siempre el portal.`,
    }),
  ),
];

export const AssistantSettingsTab = (): ReactNode => {
  const toast = useToast();
  const client = useQueryClient();

  /** Qué se está guardando, para apagar sólo lo que no debe tocarse a la vez. */
  const [busy, setBusy] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<ModelProvider, string>>({ openai: '', anthropic: '' });
  const [tests, setTests] = useState<Partial<Record<ModelProvider, { ok: boolean; detail: string }>>>({});

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const result = await getServices().assistant.get();
      return result.ok ? result.value : null;
    },
  });

  const settings = query.data;

  const apply = (next: AssistantSettings): void => {
    client.setQueryData(QUERY_KEY, next);
  };

  const choose = async (provider: AssistantProvider): Promise<void> => {
    setBusy('provider');
    const result = await getServices().assistant.setProvider(provider);
    setBusy(null);
    if (!result.ok) {
      toast.bad(`No se pudo cambiar.${reasonFor(result.error)}`);
      return;
    }
    apply(result.value);
    toast.ok(
      provider === 'none'
        ? 'El asistente entiende ahora por palabras clave.'
        : `El asistente entiende ahora con ${PROVIDER_LABEL[provider]}.`,
    );
  };

  const saveKey = async (provider: ModelProvider): Promise<void> => {
    setBusy(`key-${provider}`);
    /* Sin los espacios que se cuelan al copiar: el servidor también los quita,
       pero una llave no tiene por qué viajar con ellos. */
    const result = await getServices().assistant.setKey(provider, drafts[provider].trim());
    setBusy(null);
    if (!result.ok) {
      toast.bad(`No se pudo guardar la llave.${reasonFor(result.error)}`);
      return;
    }
    apply(result.value);
    /* El campo se vacía: lo pegado ya no hace falta en pantalla, y la prueba
       anterior era de otra llave. */
    setDrafts((current) => ({ ...current, [provider]: '' }));
    setTests((current) => ({ ...current, [provider]: undefined }));
    toast.ok(
      getServices().isDemo
        ? `Modo demo: la llave de ${PROVIDER_LABEL[provider]} no se guardó en ningún lado.`
        : `Llave de ${PROVIDER_LABEL[provider]} guardada. Pruébala antes de elegirlo.`,
    );
  };

  const removeKey = async (provider: ModelProvider): Promise<void> => {
    setBusy(`key-${provider}`);
    const result = await getServices().assistant.setKey(provider, null);
    setBusy(null);
    if (!result.ok) {
      toast.bad(`No se pudo quitar la llave.${reasonFor(result.error)}`);
      return;
    }
    apply(result.value);
    setTests((current) => ({ ...current, [provider]: undefined }));
    toast.ok(`Llave de ${PROVIDER_LABEL[provider]} quitada.`);
  };

  const test = async (provider: ModelProvider): Promise<void> => {
    setBusy(`test-${provider}`);
    const result = await getServices().assistant.test(provider);
    setBusy(null);
    setTests((current) => ({
      ...current,
      [provider]: result.ok
        ? result.value
        : { ok: false, detail: `No se pudo hacer la prueba.${reasonFor(result.error)}` },
    }));
  };

  if (query.isPending) return <Spinner label="Cargando la configuración del asistente" />;
  if (settings === null || settings === undefined) {
    return (
      <Banner tone="warn">
        No se pudo leer la configuración del asistente. Sólo el superusuario puede verla; si lo eres,
        recarga la página.
      </Banner>
    );
  }

  return (
    <div className={styles.tab}>
      {/*
        * En demo, dicho arriba y antes que nada. Sin esto la pantalla contesta
        * «llave guardada» igual que la de verdad, y ya pasó: alguien pegó su
        * llave aquí y la dio por puesta. Es la misma forma que el «Enviar prueba»
        * de los correos, que afirmaba haber enviado sin tener nada detrás.
        */}
      {getServices().isDemo && (
        <Banner tone="danger">
          Estás en modo demo: aquí <strong>no se guarda ni se prueba nada</strong>. Una llave que pegues
          no sale de esta pestaña y se pierde al recargar. Para probar una llave de verdad sin
          desplegar, corre «npm run check:model» en la terminal.
        </Banner>
      )}

      {settings.whatsapp.connected ? (
        <Banner tone="info">
          WhatsApp está conectado
          {settings.whatsapp.numberTail === null
            ? ''
            : `, con el número terminado en ${settings.whatsapp.numberTail}`}
          .
          {settings.whatsapp.dryRun &&
            ' Está en seco: el asistente anota lo que contestaría y no envía nada.'}
        </Banner>
      ) : (
        <Banner tone="warn">
          WhatsApp todavía no está conectado: falta la configuración de Meta, que pone quien despliega
          el portal. Lo de esta pantalla se puede dejar listo desde ahora.
        </Banner>
      )}

      {!settings.canStoreKeys && (
        <Banner tone="warn">
          Todavía no se pueden guardar llaves: al secreto del asistente le falta la pieza con que se
          cifran. Quien despliega tiene que correr «npm run set:whatsapp» y volver a desplegar.
        </Banner>
      )}

      <section className={styles.section} aria-labelledby="assistant-provider">
        <h3 id="assistant-provider" className={styles.heading}>
          Cómo entiende lo que le escriben
        </h3>
        <div className={styles.options} role="radiogroup" aria-labelledby="assistant-provider">
          {optionsFor(settings).map((option) => {
            const ready = option.id === 'none' || providerReady(settings, option.id);
            const selected = settings.provider === option.id;
            return (
              <label
                key={option.id}
                className={cx(styles.option, selected && styles.optionOn, !ready && styles.optionOff)}
              >
                <input
                  type="radio"
                  name="assistant-provider"
                  className={styles.radio}
                  checked={selected}
                  disabled={!ready || busy !== null}
                  onChange={() => {
                    void choose(option.id);
                  }}
                />
                <span className={styles.optionText}>
                  <span className={styles.optionTitle}>{option.title}</span>
                  <span className={styles.optionNote}>
                    {ready ? option.note : 'Guarda y prueba su llave abajo para poder elegirlo.'}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <section className={styles.section} aria-labelledby="assistant-keys">
        <h3 id="assistant-keys" className={styles.heading}>
          Llaves
        </h3>
        <p className={styles.note}>
          Una llave guardada no se vuelve a mostrar, ni a ti: sólo en qué termina. Para cambiarla, pega
          la nueva encima.
        </p>

        <div className={styles.keys}>
          {MODEL_PROVIDERS.map((provider) => {
            const key = settings.keys[provider];
            const label = PROVIDER_LABEL[provider];
            const result = tests[provider];
            const draft = drafts[provider].trim();

            return (
              <div key={provider} className={styles.key}>
                <div className={styles.keyHead}>
                  <span className={styles.keyTitle}>{label}</span>
                  {key === null ? (
                    <Chip background="var(--color-surface-subtle-alt)" foreground="var(--color-text-secondary)">
                      Sin llave
                    </Chip>
                  ) : key.readable ? (
                    <Chip background="var(--color-success-bg)" foreground="var(--color-success-ink)">
                      Guardada · termina en {key.tail}
                    </Chip>
                  ) : (
                    <Chip background="var(--color-warning-bg)" foreground="var(--color-warning-ink)">
                      Hay que volver a guardarla
                    </Chip>
                  )}
                </div>

                {key !== null && key.updatedByName.length > 0 && (
                  <p className={styles.note}>
                    La guardó {key.updatedByName}
                    {key.updatedAt === null ? '' : ` el ${DAY.format(key.updatedAt)}`}.
                  </p>
                )}

                <div className={styles.keyRow}>
                  <input
                    type="password"
                    className={cx(inputClass(), styles.keyInput)}
                    aria-label={`Llave de ${label}`}
                    placeholder={key === null ? 'Pega aquí la llave' : 'Pega aquí la llave nueva'}
                    autoComplete="off"
                    spellCheck={false}
                    value={drafts[provider]}
                    disabled={!settings.canStoreKeys}
                    onChange={(event) => {
                      setDrafts((current) => ({ ...current, [provider]: event.target.value }));
                    }}
                  />
                  <Button
                    small
                    busy={busy === `key-${provider}`}
                    disabled={draft.length === 0 || busy !== null || !settings.canStoreKeys}
                    onClick={() => {
                      void saveKey(provider);
                    }}
                  >
                    Guardar llave
                  </Button>
                </div>

                {key !== null && (
                  <div className={styles.keyActions}>
                    <Button
                      variant="secondary"
                      small
                      busy={busy === `test-${provider}`}
                      disabled={busy !== null || !key.readable}
                      onClick={() => {
                        void test(provider);
                      }}
                    >
                      Probar
                    </Button>
                    <Button
                      variant="ghost"
                      small
                      disabled={busy !== null}
                      onClick={() => {
                        void removeKey(provider);
                      }}
                    >
                      Quitar
                    </Button>
                  </div>
                )}

                {result !== undefined && (
                  <Banner tone={result.ok ? 'success' : 'danger'}>{result.detail}</Banner>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
